import {gql} from '@apollo/client'
import React, {useCallback, useState} from 'react'
import {useDebouncedCallback} from 'use-debounce'
import {TextInput} from '../../../../components/library/form'
import {Checkbox} from '../../../../components/library/form/checkbox'
import styled from 'styled-components'
import {FormModal} from '../../../../components/modals'
import {
  Section,
  SectionBody,
  SectionHeader
} from '../../../../components/sections'
import {
  PermissionAction,
  PermissionResource,
  SaveBusinessUserInput
} from '../../../../data/workbench-api/generated/types'
import {useStateChanged} from '../../../../hooks/use-state-changed'
import {useAnalytics} from '../../../../lib/analytics/analytics'
import {EMAIL_REGEX} from '../../../../lib/helpers'
import {OrganisationNode} from '../../../../organisation'
import {
  GroupsFragment,
  MemberModalFragment,
  useSaveBusinessUserMutation
} from './generated/member-modal'

const GroupIntro = styled.div`
  margin-top: 30px;
  margin-bottom: 20px;
  h4 {
    margin-bottom: 10px;
  }
  p {
    margin-top: 10px;
    margin-bottom: 20px;
  }
`
const Fields = styled.div`
  > div {
    margin-bottom: 15px;
  }
`

const GroupName = styled(SectionHeader)`
  font-weight: normal;
`

const GroupDescription = styled(SectionBody)`
  margin-left: 40px;
`

export const MemberModal = ({
  organisation,
  member,
  groups,
  handleClose,
  onSaved,
  existingEmails
}: {
  organisation: OrganisationNode
  member?: MemberModalFragment
  groups: GroupsFragment[]
  handleClose: () => void
  onSaved: () => void
  existingEmails?: string[]
}) => {
  const inviting = !member?.id
  const [state, changeState, changed] = useStateChanged<SaveBusinessUserInput>({
    organisationId: organisation.id,
    businessUserId: member?.id ?? null,
    firstName: member?.firstName ?? '',
    lastName: member?.lastName ?? '',
    phone: member?.phone ?? '',
    inviteMessage: '',
    inviteEmail: member?.email ?? '',
    groupIds:
      member?.groups.nodes
        .map(g => g.organisationGroup?.id)
        .filter((id): id is string => !!id) ?? []
  })
  const [saveBusinessUser, {loading, error: saveError}] =
    useSaveBusinessUserMutation()

  const [inputError, setInputError] = useState<string>()

  const isValid = !!(
    state.firstName &&
    !inputError &&
    (!inviting ||
      (state.lastName &&
        state.inviteEmail &&
        state.groupIds.length > 0 &&
        EMAIL_REGEX.test(state.inviteEmail)))
  )
  const wrappedHandleClose = useCallback(() => {
    if (
      !changed ||
      confirm(
        'You have unsaved changes - are you sure you want to discard them?'
      )
    ) {
      handleClose()
    }
  }, [changed, handleClose])
  const analytics = useAnalytics()

  const viewGroups =
    organisation.hasPermission(
      PermissionResource.BusinessUserGroup,
      PermissionAction.View
    ) &&
    organisation.hasPermission(PermissionResource.Group, PermissionAction.View)

  const editGroups = organisation.hasPermission(
    PermissionResource.BusinessUserGroup,
    PermissionAction.Edit
  )

  const canEdit = organisation.hasPermission(
    PermissionResource.BusinessUser,
    PermissionAction.Edit
  )

  const alreadyAUser = (email: string) =>
    email && existingEmails && existingEmails.includes(email)

  const validEmail = (email: string) => !!email.match(EMAIL_REGEX)

  const emailValidate = useDebouncedCallback((email: string) => {
    if (!validEmail(email)) {
      setInputError('Email address is invalid')
    } else if (alreadyAUser(email)) {
      setInputError('Email address is already in-use or invited')
    } else {
      setInputError('')
    }
  }, 800)

  return (
    <FormModal
      title={inviting ? `Invite new member` : `Workbench member`}
      handleClose={wrappedHandleClose}
      onSubmit={async () => {
        await saveBusinessUser({
          variables: {
            businessUser: state
          }
        })
        analytics.track({
          event: 'Invited user to org',
          properties: {
            org_id: organisation.id,
            category: 'admin'
          }
        })
        onSaved()
      }}
      readOnly={!canEdit}
      isValid={!loading && isValid}
      error={saveError?.message ?? inputError}
      submitText={inviting ? `Invite` : `Save changes`}
    >
      <Fields>
        <TextInput
          label="First name"
          readOnly={!canEdit}
          disabled={!canEdit}
          required={true}
          value={state?.firstName ?? ''}
          onChange={firstName => changeState(s => ({...s, firstName}))}
        />
        <TextInput
          label="Last name"
          required={inviting}
          readOnly={!canEdit}
          disabled={!canEdit}
          value={state?.lastName ?? ''}
          onChange={lastName => changeState(s => ({...s, lastName}))}
        />
        <TextInput
          label="Email"
          error={
            !!(
              state?.inviteEmail &&
              (!validEmail(state.inviteEmail) ||
                alreadyAUser(state.inviteEmail))
            )
          }
          required={inviting}
          readOnly={!inviting || !canEdit}
          disabled={!canEdit}
          value={state?.inviteEmail ?? ''}
          onChange={email => {
            changeState(s => ({...s, inviteEmail: email}))
            emailValidate(email)
          }}
        />
        <TextInput
          label="Phone (optional)"
          type="tel"
          readOnly={!canEdit}
          disabled={!canEdit}
          value={state?.phone ?? ''}
          onChange={phone => changeState(s => ({...s, phone}))}
        />
        {inviting && (
          <TextInput
            label="Message (optional)"
            upLabel="Add an optional invite message"
            value={state?.inviteMessage ?? ''}
            onChange={inviteMessage =>
              changeState(s => ({...s, inviteMessage}))
            }
          />
        )}
        {viewGroups && (
          <>
            {editGroups ? (
              <GroupIntro>
                <h4>Add to group</h4>
                <p>Select the groups you'd like this member to belong to</p>
              </GroupIntro>
            ) : (
              <GroupIntro>
                <h4>Group membership</h4>
                <p>Groups this member to belongs to</p>
              </GroupIntro>
            )}
            {groups.map(group => {
              return (
                <Section
                  key={group.id}
                  style={{width: '420px'}}
                  alwaysOpen={!group.description}
                >
                  <GroupName>
                    <Checkbox
                      disabled={!editGroups}
                      label={group.name}
                      checked={state.groupIds.includes(group.id)}
                      onChange={checked => {
                        changeState(s => ({
                          ...s,
                          groupIds: checked
                            ? s.groupIds.concat(group.id)
                            : s.groupIds.filter(id => id !== group.id)
                        }))
                      }}
                    />
                  </GroupName>
                  {group.description && (
                    <GroupDescription>{group.description}</GroupDescription>
                  )}
                </Section>
              )
            })}
          </>
        )}
      </Fields>
    </FormModal>
  )
}

MemberModal.fragments = {
  member: gql`
    fragment MemberModal on BusinessUser {
      id
      organisationId
      firstName
      lastName
      phone
      email
      groups: businessUserGroupsByOrganisationIdAndBusinessUserId {
        nodes {
          organisationGroup {
            id
          }
        }
      }
    }
  `,
  groups: gql`
    fragment Groups on Group {
      id
      name
      description
    }
  `
}

gql`
  mutation SaveBusinessUser($businessUser: SaveBusinessUserInput) {
    saveBusinessUser(input: $businessUser) {
      businessUser {
        ...MemberModal
      }
    }
  }
  ${MemberModal.fragments.member}
`
